/**
 * Supabase Portfolio Update Repository
 * Handles CRUD for portfolio updates via Supabase
 */

import type {
  IPortfolioUpdateRepository,
  PortfolioUpdate,
  CreatePortfolioUpdateData,
} from '../interfaces/IPortfolioUpdateRepository'
import type { CursorParams, PaginatedResult } from '@/lib/types/pagination'
import { requireSupabaseClient } from '@/lib/supabase'

interface PortfolioUpdateRow {
  id: string
  user_id: string
  screenshot_url: string | null
  portfolio_value: number | null
  positions: string | null
  commentary: string | null
  created_at: string
  updated_at: string | null
  profiles: {
    username: string
    display_name: string | null
  } | null
}

export class SupabasePortfolioUpdateRepository implements IPortfolioUpdateRepository {
  async getUpdates(): Promise<PortfolioUpdate[]> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('portfolio_updates')
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name
        )
      `
      )
      .order('created_at', { ascending: false })
      .returns<PortfolioUpdateRow[]>()

    if (error) {
      throw new Error(`Failed to fetch portfolio updates: ${error.message}`)
    }

    return (data || []).map((row) => this.mapToUpdate(row))
  }

  async getUpdatesPaginated(
    params: Pick<CursorParams, 'cursor' | 'cursorId' | 'limit'>
  ): Promise<PaginatedResult<PortfolioUpdate>> {
    const supabase = requireSupabaseClient()
    const { cursor, cursorId, limit } = params

    let query = supabase
      .from('portfolio_updates')
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name
        )
      `
      )
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(limit + 1)

    if (cursor && cursorId) {
      query = query.or(
        `created_at.lt.${cursor},and(created_at.eq.${cursor},id.lt.${cursorId})`
      )
    }

    const { data, error } = await query.returns<PortfolioUpdateRow[]>()

    if (error) {
      throw new Error(`Failed to fetch portfolio updates: ${error.message}`)
    }

    const rows = data || []
    const hasMore = rows.length > limit
    const pageRows = hasMore ? rows.slice(0, limit) : rows
    const lastRow = pageRows[pageRows.length - 1]

    return {
      data: pageRows.map((row) => this.mapToUpdate(row)),
      nextCursor: lastRow ? lastRow.created_at : null,
      nextCursorId: lastRow ? lastRow.id : null,
      hasMore,
    }
  }

  async createUpdate(
    input: CreatePortfolioUpdateData
  ): Promise<PortfolioUpdate> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('portfolio_updates')
      .insert({
        user_id: input.userId,
        screenshot_url: input.screenshotUrl || null,
        portfolio_value: input.portfolioValue || null,
        positions: input.positions || null,
        commentary: input.commentary || null,
      } as Record<string, unknown>)
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name
        )
      `
      )
      .single<PortfolioUpdateRow>()

    if (error) {
      throw new Error(`Failed to create portfolio update: ${error.message}`)
    }

    return this.mapToUpdate(data)
  }

  async deleteUpdate(id: string): Promise<void> {
    const supabase = requireSupabaseClient()

    const { error } = await supabase
      .from('portfolio_updates')
      .delete()
      .eq('id', id)

    if (error) {
      throw new Error(`Failed to delete portfolio update: ${error.message}`)
    }
  }

  private mapToUpdate(row: PortfolioUpdateRow): PortfolioUpdate {
    return {
      id: row.id,
      userId: row.user_id,
      username: row.profiles?.username || 'unknown',
      displayName: row.profiles?.display_name || undefined,
      screenshotUrl: row.screenshot_url || undefined,
      portfolioValue: row.portfolio_value || undefined,
      positions: row.positions || undefined,
      commentary: row.commentary || undefined,
      createdAt: row.created_at,
    }
  }
}
